import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from './supabaseClient';
import Auth from './Auth';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { Terms, Privacy } from './LegalPages';
import About from './About';
import AdonisianCreed from './AdonisianCreed';
import LocationAutocomplete from './LocationAutocomplete';
import MoonCalculator from './MoonCalculator';
import GreatYearTracker from './GreatYearTracker';
import Contact from './Contact';
import AscendanceDashboard from './AscendanceDashboard';
import Horoscopes from './Horoscopes';
import SynastryOracle from './SynastryOracle';
import Ephemeris from './Ephemeris';
import NatalOracle from './NatalOracle';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import GlobalCheckoutBanner from './GlobalCheckoutBanner';
import Services from './Services';
import ScrollToTop from './ScrollToTop';

// 1. Adonisian Wheel Configuration (Matches your Core Engine boundaries)
const ADONISIAN_WHEEL_CONFIG = [
  { name: "Aries", start: 28.40, end: 53.48, symbol: "♈︎" },
  { name: "Taurus", start: 53.48, end: 90.25, symbol: "♉︎" },
  { name: "Gemini", start: 90.25, end: 118.02, symbol: "♊︎" },
  { name: "Cancer", start: 118.02, end: 138.16, symbol: "♋︎" },
  { name: "Leo", start: 138.16, end: 173.86, symbol: "♌︎" },
  { name: "Virgo", start: 173.86, end: 218.01, symbol: "♍︎" },
  { name: "Libra", start: 218.01, end: 241.13, symbol: "♎︎" },
  { name: "Scorpio", start: 241.13, end: 247.72, symbol: "♏︎" },
  { name: "Ophiuchus", start: 247.72, end: 266.38, symbol: "⛎︎" },
  { name: "Sagittarius", start: 266.38, end: 299.73, symbol: "♐︎" },
  { name: "Capricorn", start: 299.73, end: 327.42, symbol: "♑︎" },
  { name: "Aquarius", start: 327.42, end: 351.83, symbol: "♒︎" },
  { name: "Pisces", start: 351.83, end: 28.40, symbol: "♓︎" }
];

const symbols = {
  "Sun": "☉", "Moon": "☽", "Mercury": "☿", "Venus": "♀", "Mars": "♂", 
  "Jupiter": "♃", "Saturn": "♄", "Uranus": "♅", "Neptune": "♆", "Pluto": "♇", 
  "Ceres": "⚳", "Chiron": "⚷", "Rising Sign": "ASC"
};

// CRITICAL: This list acts as the "Firewall" against the 653 ghost entries
const PLANET_ORDER = ["Rising Sign", "Sun", "Moon", "Mercury", "Venus", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune", "Pluto", "Ceres", "Chiron"];

function App() {
  const [formData, setFormData] = useState({
    year: 1990,
    month: 1,
    day: 1,
    localTime: "12:00", // "12:00" is the default HH:MM format
    lat: null,
    lon_geo: null,
    utcOffset: 0
  });
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState(null);
  const [meditationCount, setMeditationCount] = useState(0);
  const [matrixProfile, setMatrixProfile] = useState(null);
  const [results, setResults] = useState(null);
  const [hoverData, setHoverData] = useState(null);
  const [userPerms, setUserPerms] = useState([]);
  const [unlockedCharts, setUnlockedCharts] = useState([]);
  const [planets, setPlanets] = useState([]);
  const [somaticFocus, setSomaticFocus] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false); 
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAstroModal, setShowAstroModal] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [signLibrary, setSignLibrary] = useState({});
  const [houseLibrary, setHouseLibrary] = useState({});
  const [somaticLibrary, setSomaticLibrary] = useState({});
  const [dailyWeather, setDailyWeather] = useState(null);
  const [weatherDateOffset, setWeatherDateOffset] = useState(0);

  // --- THE IRONCLAD DISCONNECT ENGINE ---
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setMatrixProfile(null);
    setUnlockedCharts([]);
    setUserPerms([]);
    window.location.href = '/'; // Hard reload forces local storage to purge ghost tokens
  };

  // --- Create the Autocomplete Component with Google Maps API ---
  const handleLocationCalculated = (locationData) => {
    setFormData(prevData => ({
      ...prevData,
      lat: locationData.lat,
      lon_geo: locationData.lng,
      utcOffset: locationData.utcOffset
    }));
  };

  // Daily Somatic Weather Fetcher
  const fetchDailyWeather = async (offset) => {
    setDailyWeather(null); 
    
    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + offset);
    
    // THE FIX: Force Javascript to use LOCAL time, not London UTC time!
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`; 
    
    const { data, error } = await supabase
      .from('daily_somatic_weather')
      .select('*')
      .eq('date', dateString);
      
    if (error) {
      console.error("Supabase Weather Error:", error);
    } else if (data && data.length > 0) {
      setDailyWeather(data[0]);
    } else {
      setDailyWeather({ 
          title: "Signal Lost", 
          reading: "The planetary telemetry for this date has not been compiled yet.", 
          somatic_focus: "Stillness" 
      });
    }
  };

  // --- MASTER EFFECT HANDLERS ---
  
  useEffect(() => {
    fetchDailyWeather(weatherDateOffset);
  }, [weatherDateOffset]);

  // --- HOMEPAGE PASSIVE WHEEL ROTATION STATE ---
  const [passiveRotation, setPassiveRotation] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setPassiveRotation((prev) => (prev + 0.2) % 360);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  const loadAdonisianData = async () => {
   try {
     const response = await fetch('/api/calculate?year=1990&month=1&day=1&hour_utc=12.0&lat=37.77&lon_geo=-122.41');
     if (!response.ok) return;
     const data = await response.json();
     
     // 👇 Shifted the default background state from Rising Sign to Sun
     if (data && data["Sun"]) {
       setSomaticFocus(data["Sun"].somatic || "Spinal Gateway Engine");
     }
   } catch (error) {
     console.log("Initialization data alignment verified.");
   }
  };

  // --- THE NEW TRUTH-SEEKING MATH ENGINE (GAMIFICATION) ---
  const fetchMeditationCount = async (userSession) => {
    const activeUser = userSession?.user || session?.user;
    if (!activeUser) return;

    // 1. Fetch all raw logs (Asking ONLY for created_at to prevent Supabase column errors)
    const { data: logsData, error: logsError } = await supabase
      .from('meditation_logs')
      .select('created_at')
      .eq('user_id', activeUser.id);

    let trueLifetimeCount = 0;
    let trueUnlockedKeys = [];
    let currentMonthPoints = 0;

    if (logsError) {
      console.error("Matrix Glitch (Logs):", logsError);
    } else if (logsData) {
      trueLifetimeCount = logsData.length;
      setMeditationCount(trueLifetimeCount);

      // 2. Group all historical logs mathematically by YYYY-MM
      const monthlyCounts = {};
      logsData.forEach(log => {
        const logDate = log.created_at || new Date().toISOString();
        const monthStr = logDate.substring(0, 7); // Extracts "2026-07"
        monthlyCounts[monthStr] = (monthlyCounts[monthStr] || 0) + 1;
      });

      // 3. Calculate how many keys they actually earned
      let keysEarned = 0;
      Object.values(monthlyCounts).forEach(count => {
        if (count >= 10) keysEarned += 1; // 1 key awarded for every month with 10+ logs!
      });

      // Cap at 13 keys so the UI doesn't break if you become a super-master
      trueUnlockedKeys = PLANET_ORDER.slice(0, Math.min(keysEarned, 13));

      // 4. Get this exact month's progress
      const currentMonthStr = new Date().toISOString().substring(0, 7);
      currentMonthPoints = monthlyCounts[currentMonthStr] || 0;
    }

    // 5. Fetch the Gamification Matrix Profile
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', activeUser.id)
      .single();
      
    // 6. Bulletproof Hydration (Even if the profile row doesn't exist yet, show the keys!)
    if (profileError && profileError.code === 'PGRST116') {
       // Profile doesn't exist yet, construct a default
       setMatrixProfile({
          alias: "Master-Builder",
          lifetime_meditations: trueLifetimeCount,
          current_month_qualifying_count: currentMonthPoints,
          unlocked_keys_array: trueUnlockedKeys
       });
    } else if (profileData && !profileError) {
      setMatrixProfile({
        ...profileData,
        lifetime_meditations: trueLifetimeCount,
        current_month_qualifying_count: currentMonthPoints,
        unlocked_keys_array: trueUnlockedKeys
      });
    }
  };

  const logMeditation = async () => {
    const user = session?.user; 
    if (!user) {
      alert("No active session found. The vault is locked.");
      return;
    }

    // 1. Log the actual meditation to the historical ledger (The Source of Truth)
    const { error: logError } = await supabase
      .from('meditation_logs')
      .insert([{ user_id: user.id }]);

    if (logError) {
      console.error("Ascendance interrupted:", logError);
      alert("Ascendance interrupted. Try again.");
      return;
    }

    alert("Somatic Meditation Logged! CSF Flow optimized. 🧘‍♂️");
    
    // 2. Trigger the master fetch with the current session so it doesn't use stale state!
    fetchMeditationCount({ user }); 
  };
  
  const ascDegree = results?.["Rising Sign"]?.degree || 0;
  const rotationOffset = 90 - ascDegree;
  const getRotatedDegree = (deg) => (deg + rotationOffset + 360) % 360;

  const fetchPlanets = async () => {
    try {
      const response = await fetch('/api/calculate?year=1990&month=1&day=1&hour_utc=12.0&lat=37.77&lon_geo=-122.41');
      if (!response.ok) return;
      const data = await response.json();
      if (data) {
        const compiledPlanets = Object.entries(data).map(([name, details]) => ({
          name,
          ...details
        }));
        setPlanets(compiledPlanets);
      }
    } catch (error) {
      console.log("Core coordinate spaces synced.");
    }
  };

  const handleUpgradeCheckout = async () => {
    if (!session?.user?.id) {
      alert("Please log in to unlock the Deluxe Report.");
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
        userId: session.user.id,
        userEmail: session.user.email,
        sku: "deluxe_report",
        year: parseInt(formData.year) || 1990,
        month: parseInt(formData.month) || 1,
        day: parseInt(formData.day) || 1,
        hour_utc: parseFloat(formData.hour_utc) || 12.0,
        lat: parseFloat(formData.lat) || 37.77,
        lon_geo: parseFloat(formData.lon_geo) || -122.41
        }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to generate checkout link.");
      }
    } catch (err) {
      console.error("Payment Gateway alignment failed:", err);
      alert("Could not connect to payment vault. Please try again.");
      setCheckoutLoading(false);
    }
  };
  
  // --- THE GLOBAL BANNER CHECKOUT FLOW (FRICTIONLESS GUEST MODE) ---
  const handleGlobalCheckout = async () => {
    // 1. NO GATEKEEPER. Instant teleportation to Stripe. 
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // If they aren't logged in, we flag them as a guest and pass null for email 
          // so Stripe natively prompts them to type their email on the checkout page!
          userId: session?.user?.id || "guest_checkout",
          userEmail: session?.user?.email || null, 
          sku: "global_deluxe_blueprint",
          year: parseInt(formData.year) || 1990,
          month: parseInt(formData.month) || 1,
          day: parseInt(formData.day) || 1,
          hour_utc: parseFloat(formData.hour_utc) || 12.0,
          lat: parseFloat(formData.lat) || 37.77,
          lon_geo: parseFloat(formData.lon_geo) || -122.41
        }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to generate checkout link.");
      }
    } catch (err) {
      console.error("Payment Gateway alignment failed:", err);
      alert("Could not connect to the payment vault. Please try again.");
    } 
  };

 const handleCalculate = async (e) => {
    if (e) e.preventDefault(); 
    
    setLoading(true);

    const timeToSplit = formData.localTime || "12:00";
    const [hours, minutes] = timeToSplit.split(':').map(Number);
    const localDecimalHour = hours + (minutes / 60);
    const finalUtcHour = localDecimalHour - formData.utcOffset;

    const backendPayload = {
      year: formData.year,
      month: formData.month,
      day: formData.day,
      hour_utc: finalUtcHour, 
      lat: formData.lat,
      lon_geo: formData.lon_geo
    };

    try {
      const response = await axios.get('/api/calculate', { params: backendPayload });
      setResults(response.data);
    } catch (err) {
      console.error("Engine failed to ignite:", err);
      alert("Cosmic interference: The engine failed to calculate your chart. Please try again.");
    } finally {
      setLoading(false); 
    }
  };

  const copyBlueprint = () => {
    const header = `--- ADONISIAN SOMATIC BLUEPRINT ---\n`;
    const subHeader = `Calculated on: ${new Date().toLocaleDateString()} | System: 13-Sign True Astronomy\n`;
    const divider = `-----------------------------------\n`;

    const bodyData = results ? PLANET_ORDER
      .map((body) => {
        // 🛑 THE FIREWALL: Prevent free users from copying locked data!
        const isUnlocked = isCurrentChartUnlocked || body === "Sun";
        if (!isUnlocked) return null;

        const data = results[body];
        if (!data) return null;
        const deg = data?.degree != null ? data.degree.toFixed(2) : "0.00";
        const house = data?.house || "?";
        const sign = data?.sign || "Unknown";

        return [
          `${body.toUpperCase()}`,
          `  Position:  House ${house} (${sign})`,
          `  Degree:    ${deg}°`,
          `  Quality:   ${data?.element || "N/A"} | ${data?.modality || "N/A"}`,
          `  Somatic:   ${data?.somatic || "N/A"}`,
          `  Chakra:    ${data?.chakra || "N/A"}`,
          `  Energy:    ${data?.energy || "N/A"}`,
          `-----------------------------------`
        ].join('\n');
      })
      .filter(Boolean)
      .join('\n') : "No data available";

    const copyright = `\n\n© ${new Date().getFullYear()} Adonisian Astrology by Adonis Ramirez.\nDeveloped in the San Francisco Bay Area, CA.\nAll rights reserved.`;
    const fullText = header + subHeader + divider + bodyData + copyright;

    navigator.clipboard.writeText(fullText)
      .then(() => alert("Adonisian Somatic Blueprint Locked & Loaded! 🧬"))
      .catch(err => console.error('Could not copy text: ', err));
  };

  const useCurrentSkies = () => {
    const now = new Date();
    const currentData = {
      year: now.getUTCFullYear(),
      month: now.getUTCMonth() + 1,
      day: now.getUTCDate(),
      hour_utc: now.getUTCHours() + (now.getUTCMinutes() / 60),
      lat: 37.76, 
      lon_geo: -122.42 
    };
    setFormData(currentData);
    
    axios.get('/api/calculate', { params: currentData })
      .then(res => setResults(res.data))
      .catch(err => console.error(err));
  };

  const fetchPermissions = async (userSession) => {
    const userId = userSession?.user?.id;
    if (!userId) return;
    
    const { data, error } = await supabase
      .from('user_permissions')
      .select('*')
      .eq('user_id', userId);

    if (error) {
      console.error("❌ SUPABASE ERROR:", error.message);
    } else {
      const activeSkus = data.filter(p => p.is_active).map(p => p.sku);
      setUserPerms(activeSkus);
    }
  };

  const fetchUnlockedCharts = async (userSession) => {
    const userId = userSession?.user?.id;
    if (!userId) return;

    const { data: chartData, error } = await supabase
      .from('unlocked_charts')
      .select('*')
      .eq('user_id', userId);
      
    if (!error && chartData) {
      setUnlockedCharts(chartData);
    }
  };

  const fetchAllLibraries = async () => {
    const [signs, houses, somatic] = await Promise.all([
      supabase.from('library_signs').select('*'),
      supabase.from('library_houses').select('*'),
      supabase.from('library_somatic').select('*')
    ]);

    if (signs.data && signs.data.length === 0) {
      alert("🚨 MATRIX GLITCH: Supabase is blocking 'library_signs'. It returned 0 rows! Check your Row Level Security (RLS) policies.");
    }
    if (somatic.data && somatic.data.length === 0) {
      alert("🚨 MATRIX GLITCH: Supabase is blocking 'library_somatic'. It returned 0 rows! Check your Row Level Security (RLS) policies.");
    }

    const sMap = {};
    signs.data?.forEach(i => {
      if(i.planet && i.sign) {
        const key = `${i.planet.trim()}_${i.sign.trim()}`;
        sMap[key] = i.interpretation;
      }
    });
    setSignLibrary(sMap);

    const hMap = {};
    houses.data?.forEach(i => {
      if(i.planet && i.house) {
        // If the row has a sign, lock it to the sign. Otherwise, use the standard fallback.
        const safeSign = i.sign ? `_${i.sign.trim()}` : "";
        const key = `${i.planet.trim()}_${String(i.house).trim()}${safeSign}`;
        hMap[key] = i.interpretation;
      }
    });
    setHouseLibrary(hMap);

    const bMap = {};
    somatic.data?.forEach(i => {
      if(i.planet && i.sign) {
        const key = `${i.planet.trim()}_${i.sign.trim()}`;
        bMap[key] = i.interpretation;
      }
    });
    setSomaticLibrary(bMap);
  };

  // --- 2. MASTER INITIALIZATION HOOK ---
  useEffect(() => {
    loadAdonisianData();
    fetchPlanets();
    fetchAllLibraries();
    fetchDailyWeather(0);

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchMeditationCount(session); 
        fetchPermissions(session);
        fetchUnlockedCharts(session); 
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchMeditationCount(session);
        fetchPermissions(session);
        fetchUnlockedCharts(session);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const isCurrentChartUnlocked = unlockedCharts.some(chart => 
    chart.birth_year === parseInt(formData.year) &&
    chart.birth_month === parseInt(formData.month) &&
    chart.birth_day === parseInt(formData.day) &&
    parseFloat(chart.birth_hour_utc) === parseFloat(formData.hour_utc) &&
    parseFloat(chart.latitude) === parseFloat(formData.lat) &&
    parseFloat(chart.longitude) === parseFloat(formData.lon_geo)
  ) || session?.user?.email === 'eduardoadonis@gmail.com';

  return (
    <HelmetProvider> 
    <Router>
      <ScrollToTop />
      <div className="App">

        <Helmet>
          <title>Adonisian Astrology | The True 13-Sign Astro-Somatic™ Matrix</title>
          <meta name="description" content="Calculate your true astronomical placements. An Astro-Somatic™ bio-hacking framework mapping the 13-sign celestial matrix to your cerebrospinal fluid rhythms." />
        </Helmet>

  


        {/* --- GLOBAL HEADER BAR WITH HAMBURGER --- */}
        <header style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          width: '100%', 
          padding: '15px 25px', 
          boxSizing: 'border-box',
          borderBottom: '1px solid #30363d', 
          marginBottom: '20px',
          background: '#0d1117'
        }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button 
              onClick={() => setIsNavOpen(!isNavOpen)}
              style={{ background: 'transparent', border: 'none', color: '#00d4ff', fontSize: '1.8rem', cursor: 'pointer', padding: 0 }}
            >
              ☰
            </button>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <h1 className="header-title" style={{ margin: 0, color: '#00d4ff', fontSize: '1.4rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Adonisian Astrology Portal
              </h1>
            </Link>
          </div>

          <div style={{ textAlign: 'center', display: 'flex', gap: '15px' }}>
            {session ? (
              <>
                <Link to="/sanctuary" style={{ backgroundColor: '#00d4ff', color: '#0d1117', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', fontWeight: 'bold', boxShadow: '0 0 15px rgba(0, 212, 255, 0.2)' }}>
                  🏛️ The Sanctuary
                </Link>
                <button 
                  onClick={handleSignOut}
                  style={{ backgroundColor: 'transparent', color: '#ffca28', padding: '8px 16px', borderRadius: '6px', border: '1px solid #ffca28', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Close Vault
                </button>
              </>
            ) : (
              <button 
                onClick={() => setShowLoginModal(!showLoginModal)}
                style={{ backgroundColor: 'transparent', color: '#ffca28', padding: '8px 16px', borderRadius: '6px', border: '1px solid #ffca28', cursor: 'pointer', fontWeight: 'bold' }}
              >
                {showLoginModal ? "Close Vault" : "Access Vault (Log In)"}
              </button>
            )}
          </div>
        </header>

        {/* --- THE DROPDOWN MENU --- */}
        {isNavOpen && (
          <div style={{ position: 'fixed', top: '65px', left: '20px', background: '#05070a', border: '1px solid #00d4ff', borderRadius: '8px', padding: '20px', zIndex: 999, boxShadow: '0 5px 30px rgba(0, 212, 255, 0.15)', display: 'flex', flexDirection: 'column', gap: '15px', minWidth: '220px' }}>
             
      {session && (
  <>
    <Link to="/sanctuary" onClick={() => setIsNavOpen(false)} style={{ color: '#ffffff', textDecoration: 'none', fontWeight: 'bold' }}>🏛️ The Sanctuary</Link>
    {session?.user?.email === 'eduardoadonis@gmail.com' && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <Link to="/oracle" onClick={() => setIsNavOpen(false)} style={{ color: '#ffca28', textDecoration: 'none', fontWeight: 'bold' }}>👁️ Master Oracle (Synastry)</Link>
        <Link to="/natal-blueprint" onClick={() => setIsNavOpen(false)} style={{ color: '#00d4ff', textDecoration: 'none', fontWeight: 'bold', borderBottom: '1px solid #30363d', paddingBottom: '10px' }}>🧬 Master Oracle (Natal)</Link>
      </div>
    )}
  </>
)}
             <Link to="/sanctuary" onClick={() => setIsNavOpen(false)} style={{ color: '#ffffff', textDecoration: 'none', fontWeight: 'bold', borderBottom: '1px solid #30363d', paddingBottom: '10px' }}>🏛️ The Sanctuary</Link>
             <Link to="/ephemeris" onClick={() => setIsNavOpen(false)} style={{ color: '#b3ffe6', textDecoration: 'none' }}>🌌 13-Sign Ephemeris</Link>
             <Link to="/horoscopes" onClick={() => setIsNavOpen(false)} style={{ color: '#00d4ff', textDecoration: 'none' }}>🔮 Monthly Horoscopes</Link>
             <Link to="/moon-calendar" onClick={() => setIsNavOpen(false)} style={{ color: '#00ff41', textDecoration: 'none' }}>🌑 26 Moon Calendar</Link>
             <Link to="/great-year" onClick={() => setIsNavOpen(false)} style={{ color: '#ffca28', textDecoration: 'none' }}>🪐 Sign Ages & The Great Year</Link>
             <Link to="/about" onClick={() => setIsNavOpen(false)} style={{ color: '#00d4ff', textDecoration: 'none' }}>📖 About the Data</Link>
             <Link to="/creed" onClick={() => setIsNavOpen(false)} style={{ color: '#00ff41', textDecoration: 'none' }}>⚡ Wake Up (The Creed)</Link>
             <Link to="/contact" onClick={() => setIsNavOpen(false)} style={{ color: '#ff4081', textDecoration: 'none' }}>📡 Transmission (Contact)</Link>
          </div>
        )}

        {/* --- FLOATING LOGIN MODAL --- */}
        {!session && showLoginModal && (
          <div style={{ position: 'fixed', top: '80px', right: '20px', zIndex: 9999, width: '100%', maxWidth: '350px', background: '#0d1117', padding: '25px', borderRadius: '8px', border: '1px solid #ffca28', boxShadow: '0 0 30px rgba(255, 202, 40, 0.4)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ color: '#ffca28', margin: 0, letterSpacing: '1px', textTransform: 'uppercase' }}>
                Access The Vault
              </h3>
              <button onClick={() => setShowLoginModal(false)} style={{ background: 'transparent', border: 'none', color: '#8b949e', fontSize: '1.2rem', cursor: 'pointer' }}>
                ✕
              </button>
            </div>
            <Auth />
          </div>
        )}
  
  {/* --- ASTROSOMATIC DEFINITION MODAL --- */}
        {showAstroModal && (
          <div style={{ position: 'fixed', top: '0', left: '0', width: '100%', height: '100vh', background: 'rgba(0,0,0,0.8)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(5px)' }}>
            <div style={{ background: '#0d1117', border: '1px solid #00d4ff', padding: '30px', borderRadius: '12px', maxWidth: '450px', width: '90%', position: 'relative', boxShadow: '0 0 40px rgba(0, 212, 255, 0.2)', textAlign: 'left' }}>
              <button onClick={() => setShowAstroModal(false)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'transparent', border: 'none', color: '#8b949e', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
              
              <h3 style={{ color: '#00d4ff', marginTop: '0', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '15px' }}>
                Astrosomatics <span style={{fontSize: '0.8rem', color: '#8b949e', textTransform: 'lowercase', letterSpacing: '0'}}>[ as-tro-so-mat-iks ]</span>
              </h3>
              
              <p style={{ color: '#c9d1d9', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '15px' }}>
                <strong style={{color: '#ffffff'}}>Astro:</strong> Pertaining to the stars and the macro-mechanics of the cosmos.<br/>
                <strong style={{color: '#ffffff'}}>Somatics:</strong> Relating to the physical body, especially as perceived from within.
              </p>
              
              <div style={{ background: 'rgba(0, 212, 255, 0.05)', padding: '15px', borderRadius: '8px', borderLeft: '3px solid #00d4ff', marginBottom: '15px' }}>
                <p style={{ color: '#ffca28', fontSize: '0.95rem', lineHeight: '1.6', margin: 0, fontStyle: 'italic' }}>
                  To bridge the gap between ancient cosmic awareness and neuroscientific consciousness, Adonisian Astrology required the creation of an entirely new terminology.
                </p>
              </div>

              <p style={{ color: '#8b949e', fontSize: '0.95rem', lineHeight: '1.6', margin: 0 }}>
                <strong style={{color: '#ffffff'}}>Adonisian Definition:</strong> The practice of utilizing exact celestial rhythms—naturally mirrored in human physiology—for optimal performance. This is achieved by activating crucial astrobiological nodes through a synthesis of multiple modalities, including targeted exercise, cerebrospinal fluid meditation, and ancient Bija mantras.
              </p>
            </div>
          </div>
        )}
        
{/* --- DYNAMIC PAGE CONTENT --- */}
        <div style={{ flex: '1 0 auto', width: '100%', maxWidth: '100vw', boxSizing: 'border-box', overflowX: 'hidden' }}>          
          <Routes>
            <Route path="/" element={
              <main className="App-header">

                <Helmet>
                  <title>Adonisian Astrology | The True 13-Sign Astro-Somatic™ Matrix</title>
                  <meta name="description" content="Calculate your true astronomical placements. An Astro-Somatic™ bio-hacking framework mapping the 13-sign celestial matrix to your cerebrospinal fluid rhythms." />
                </Helmet>

                {/* --- ADONISIAN NARRATIVE FUNNEL LAYER --- */}
                <div className="funnel-narrative-container" style={{ maxWidth: '90%', margin: '0 auto 20px auto', padding: '0 20px', textAlign: 'left' }}>
                  
                  <h2 style={{ color: '#ffca28', fontSize: '1.7rem', fontWeight: '300', marginBottom: '15px', letterSpacing: '1px', textAlign: 'center' }}>
                    The Astro-Somatic Bio-Hacking Framework That Maps the True 13-Sign Celestial Matrix to Cerebrospinal Fluid Rhythms & Joint Matrices.
                  </h2>
                  <h3 style={{ color: '#f7f5ef', fontSize: '1.3rem', fontWeight: '300', marginBottom: '15px', letterSpacing: '1px', textAlign: 'center' }}>
                    Your Astrological Identity is a Lie.
                  </h3>
                  <h4 style={{ color: '#00d4ff', fontSize: '1.1rem', fontWeight: '400', marginBottom: '25px', letterSpacing: '0.5px', textAlign: 'center', fontStyle: 'italic' }}>
                    Searching for "Why doesn't my zodiac sign fit me?" or "Somatic healing for joint pain?" The answer lies in your true astronomical coordinates.
                  </h4>
                </div>


                {/* --- PUBLIC CALCULATION ENGINE MOVED TO THE TOP --- */}
                <div className="form-container">
          
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                    <div className="input-group" style={{ flex: '1 1 20%' }}>
                      <label style={{ color: '#8b949e', fontSize: '0.85rem' }}>Year</label>
                      <input type="number" value={formData.year} onChange={e => setFormData({...formData, year: e.target.value})} style={{ width: '100%', padding: '10px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    <div className="input-group" style={{ flex: '1 1 20%' }}>
                      <label style={{ color: '#8b949e', fontSize: '0.85rem' }}>Month</label>
                      <input type="number" value={formData.month} onChange={e => setFormData({...formData, month: e.target.value})} style={{ width: '100%', padding: '10px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    <div className="input-group" style={{ flex: '1 1 20%' }}>
                      <label style={{ color: '#8b949e', fontSize: '0.85rem' }}>Day</label>
                      <input type="number" value={formData.day} onChange={e => setFormData({...formData, day: e.target.value})} style={{ width: '100%', padding: '10px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    
                    <div className="input-group" style={{ flex: '1 1 30%' }}>
                      <label style={{ color: '#8b949e', fontSize: '0.85rem' }}>Exact Local Time</label>
                      <input 
                        type="time" 
                        value={formData.localTime} 
                        onChange={e => setFormData({...formData, localTime: e.target.value})} 
                        style={{ width: '100%', padding: '10px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '4px', boxSizing: 'border-box', colorScheme: 'dark' }} 
                      />
                    </div>
                  </div>

                  <div className="input-group" style={{ width: '100%', marginBottom: '20px' }}>
                    <label style={{ color: '#8b949e', fontSize: '0.85rem', display: 'block', marginBottom: '5px' }}>Birth City</label>
                    <LocationAutocomplete 
                      onLocationCalculated={(loc) => setFormData(prevData => ({
                        ...prevData, 
                        lat: loc.lat, 
                        lon_geo: loc.lng, 
                        utcOffset: loc.utcOffset 
                      }))} 
                    />
                  </div>
                  <button 
                    onClick={(e) => handleCalculate(e)} 
                    disabled={loading}
                    style={{
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? "Aligning Coordinates..." : "Calculate Somatic Blueprint"}
                  </button>
                  <button onClick={useCurrentSkies} style={{backgroundColor: '#9400d3', color: 'white', marginLeft: '10px'}}>
                    Live Somatic Weather
                  </button>
                  {results && session && (
                    <button onClick={copyBlueprint} className="copy-button">
                      Copy Blueprint
                    </button>
                  )}
                </div>

                {/* --- INTERACTIVE WHEEL DISPLAY (PUBLIC) --- */}
                {results && (
                  <div className="portal-layout">
                    <p style={{
                      color: '#8b949e',
                      fontSize: '0.85rem',
                      textAlign: 'center',
                      margin: '15px auto 30px auto',
                      maxWidth: '500px',
                      fontStyle: 'italic',
                      lineHeight: '1.5'
                    }}>
                      ✨ <strong style={{color: '#00ff41'}}>Interactive Matrix:</strong> Select any planetary node icon on the perimeter of the wheel to isolate its specific spinal track, chakra nexus, and active Bija mantra.
                    </p>
                    <div className="wheel-container">
                      <svg viewBox="0 0 100 100" width="100%" height="100%" aria-label="Interactive Astro-Somatic Calculation Engine for Cerebrospinal Fluid Meditation" role="img">
                        <title>Astro-Somatic Calculation Engine</title>
                        {ADONISIAN_WHEEL_CONFIG.map((sign) => {
                          const rotatedStart = getRotatedDegree(sign.start);
                          const rotatedEnd = getRotatedDegree(sign.end);
                          const startAngle = (rotatedStart - 90) * (Math.PI / 180);
                          const endAngle = (rotatedEnd - 90) * (Math.PI / 180);
                          const midAngle = ((rotatedStart + (rotatedEnd < rotatedStart ? rotatedEnd + 360 : rotatedEnd)) / 2 - 90) * (Math.PI / 180);
                          const labelR = 43; 
                          const symbolR = 32; 
                          const lx = 50 + labelR * Math.cos(midAngle);
                          const ly = 50 + labelR * Math.sin(midAngle);
                          const sx = 50 + symbolR * Math.cos(midAngle);
                          const sy = 50 + symbolR * Math.sin(midAngle);
                          return (
                            <g key={sign.name}>
                              <path d={`M 50 50 L ${50 + 45 * Math.cos(startAngle)} ${50 + 45 * Math.sin(startAngle)}`} stroke="#30363d" strokeWidth="0.2" />
                              <text x={sx} y={sy} fontSize="3" fill="#00d4ff" textAnchor="middle" dominantBaseline="middle">{sign.symbol}</text>
                              <text x={lx} y={ly} fontSize="1.5" fill="#8b949e" textAnchor="middle" transform={`rotate(${(midAngle * 180 / Math.PI) + 90}, ${lx}, ${ly})`}>
                                {sign.name.toUpperCase()}
                              </text>
                            </g>
                          );
                        })}

                        {Object.entries(results).map(([name, data]) => {
                          if (data?.degree == null) return null;
                          const rotatedPlanetDeg = getRotatedDegree(data.degree);
                          const angle = (rotatedPlanetDeg - 90) * (Math.PI / 180);
                          const r = 38;
                          const x = 50 + r * Math.cos(angle);
                          const y = 50 + r * Math.sin(angle);

                          if (isNaN(x) || isNaN(y)) return null;

                          return (
                            <g key={name} className={`planet-point ${hoverData?.name === name ? 'active-hover' : ''}`} onMouseEnter={() => setHoverData({ name, ...data })} onMouseLeave={() => setHoverData(null)}>
                              <circle cx={x} cy={y} r="5" fill="transparent" /> 
                              <circle cx={x} cy={y} r="1.5" fill={hoverData?.name === name ? "#ffca28" : "#00d4ff"} />
                              <text x={x} y={y - 4} fontSize="4.5" fill="white" textAnchor="middle">{symbols[name] || "★"}</text>
                            </g>
                          );
                        })}
                      </svg>

                      <div className="somatic-center">
                        {hoverData ? (
                          <div className="hover-info">
                            <span className="hover-name">{hoverData.name}</span>
                            <span className="hover-somatic">{hoverData.somatic}</span>
                            <span className="hover-meta">{hoverData.chakra} • {hoverData.energy}</span>
                          </div>
                        ) : (
                          <span>Somatic<br/>Engine</span>
                        )}
                      </div>
                    </div>

                    {/* --- BLUEPRINT GRID (CONDITIONAL FIREWALL) --- */}
                    <div className="blueprint-grid">
                      {session ? (
                        <>
                          {PLANET_ORDER.map((body) => {
                            const data = results[body];
                            if (!data) return null;

                            const isSun = body === "Sun";
                            const isUnlocked = isCurrentChartUnlocked || isSun;

                            return (
                              <div key={`card-${body}`} className="body-card" style={!isUnlocked ? { borderColor: '#222' } : {}}>
                                <div className="card-technical-data">
                                  
                                  <div className="card-header">
                                    <span className="symbol" style={{ color: '#ffffff' }}>{symbols[body] || "★"}</span>
                                    <h3 style={{ color: '#00d4ff', margin: '10px 0' }}>{body}</h3>
                                  </div>

                                  <div style={!isUnlocked ? { filter: 'blur(5px)', opacity: 0.4, userSelect: 'none', pointerEvents: 'none' } : {}}>
                                    <div className="card-detail"><span style={{ color: '#8b949e' }}>Sign:</span> <strong style={{ color: '#ffffff' }}>{data?.sign || "Unknown"}</strong></div>
                                    <div className="card-detail">
                                      <span style={{ color: '#8b949e' }}>Degree:</span> 
                                      <strong style={{ color: '#ffffff' }}>{data?.degree != null ? data.degree.toFixed(2) : "0.00"}°</strong>
                                    </div>
                                    <div className="card-detail"><span style={{ color: '#8b949e' }}>House:</span> <strong style={{ color: '#ffffff' }}>{data?.house || "?"}</strong></div>

                                    <div className="traits-container" style={{ marginTop: '15px' }}>
                                      <span key={`${body}-elem`} className="trait-tag" data-element={data.element}>{data.element}</span>
                                      <span key={`${body}-mod`} className="trait-tag">{data.modality}</span>
                                    </div>
                                    
                                    <div className="somatic-highlight" style={{ marginTop: '15px', color: '#ffca28', fontWeight: 'bold' }}>
                                      {data.somatic}
                                    </div>
                                  </div>
                                </div>

                                <div className="card-scripture-content" style={{ position: 'relative' }}>
                                  {isUnlocked ? (
                                    <div className="scripture-reveal">
                                      <h4 style={{ color: '#ffca28', letterSpacing: '2px', borderBottom: '1px solid rgba(255,202,40,0.3)', paddingBottom: '5px', marginBottom: '15px' }}>
                                        ✨ ADONISIAN SCRIPTURE
                                      </h4>
                                      {(() => {
                                        const lookupBody = body === "Rising Sign" ? "Rising" : body.trim();
                                        const safeSign = data.sign ? data.sign.trim() : "";
                                        const safeHouse = data.house ? String(data.house).trim() : "";
                                        const signKey = `${lookupBody}_${safeSign}`;
                                        const houseKey = `${lookupBody}_${safeHouse}`;
                                        const fallbackHouseKey = `${lookupBody}_${safeHouse}`;
                                        
                                        return (
                                          <div key={`scripture-text-${body}`}>
                                            <p style={{ marginBottom: '12px', fontSize: '0.95rem', color: '#f5fafa', lineHeight: '1.6' }}>
                                              {signLibrary[signKey] || `Spirit channeling for ${signKey} in progress...`}
                                            </p>
                                            <p style={{ marginBottom: '12px', fontSize: '0.95rem', color: '#f5fafa', lineHeight: '1.6' }}>
                                              {houseLibrary[houseKey] || houseLibrary[fallbackHouseKey] || `House alignment for ${lookupBody} in House ${safeHouse} pending...`}
                                            </p>
                                            <p style={{ fontStyle: 'italic', color: '#00d4ff', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                              {somaticLibrary[signKey] || `Somatic protocol for ${signKey} pending...`}
                                            </p>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  ) : (
                                    <div key={`upgrade-${body}`} style={{ marginTop: '15px', padding: '15px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,202,40,0.2)', textAlign: 'center' }}>
                                      <span style={{ fontSize: '2rem', display: 'block', marginBottom: '10px' }}>🔒</span>
                                      <h4 style={{ color: '#00d4ff', marginBottom: '5px' }}>
                                        Matrix Core Locked
                                      </h4>
                                      <p style={{ fontSize: '0.85rem', color: '#8b949e', lineHeight: '1.5', margin: 0 }}>
                                        Somatic protocols, elemental data, and active Bija mantras are securely encrypted.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}

                          {!isCurrentChartUnlocked && (
                            <div style={{ gridColumn: '1 / -1', margin: '40px auto', width: '100%', maxWidth: '700px', padding: '40px', background: 'linear-gradient(145deg, #05070a 0%, #0a0f14 100%)', border: '1px solid #ffca28', borderRadius: '8px', textAlign: 'center', boxShadow: '0 0 20px rgba(255, 202, 40, 0.15)' }}>
                              <h2 style={{ color: '#ffffff', fontFamily: 'monospace', marginBottom: '15px', fontSize: '1.5rem', letterSpacing: '1px' }}>
                                [ ESCAPE THE SIMULATION ]
                              </h2>
                              <p style={{ color: '#8b949e', marginBottom: '25px', lineHeight: '1.6' }}>
                                {unlockedCharts.length > 0 
                                  ? "You have entered new cosmic coordinates. Secure this map to reveal the remaining somatic keys."
                                  : "Gain absolute backend access to all somatic meridian paths, planetary keys, and automated prophecies for a single lifetime contribution."}
                              </p>
                              <button 
                                onClick={handleUpgradeCheckout}
                                disabled={checkoutLoading}
                                style={{
                                  backgroundColor: checkoutLoading ? '#30363d' : '#ffca28',
                                  color: checkoutLoading ? '#8b949e' : '#0d1117',
                                  border: 'none', padding: '15px 35px', fontSize: '1.1rem', fontWeight: 'bold', textTransform: 'uppercase', borderRadius: '4px', cursor: checkoutLoading ? 'not-allowed' : 'pointer', boxShadow: '0 0 10px rgba(255, 202, 40, 0.3)', transition: 'all 0.3s ease'
                                }}
                              >
                                {checkoutLoading ? "⚡ SECURING VAULT..." : (unlockedCharts.length > 0 ? "Unlock this map — $99" : "Access the Vault — $99")}
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '30px' }}>
                          
                          <div className="body-card" style={{ maxWidth: '600px', margin: '0 auto', border: '1px solid #ffca28', boxShadow: '0 0 20px rgba(255, 202, 40, 0.15)' }}>
                            <div className="card-technical-data">
                              <div className="card-header">
                                <span className="symbol" style={{ color: '#ffffff' }}>{symbols["Sun"]}</span>
                                <h3 style={{ color: '#ffca28', margin: '10px 0', letterSpacing: '1px' }}>ASTRONOMIC REALITY CHECK</h3>
                              </div>
                              <p style={{ color: '#8b949e', fontSize: '0.9rem', margin: '0 0 15px 0', fontStyle: 'italic' }}>
                                The universe doesn't lie. According to exact cosmic coordinates at your moment of birth:
                              </p>
                              <div className="card-detail"><span style={{ color: '#8b949e' }}>Your True Sun Sign:</span> <strong style={{ color: '#00d4ff', fontSize: '1.2rem' }}>{results["Sun"]?.sign || "Unknown"}</strong></div>
                              <div className="card-detail">
                                <span style={{ color: '#8b949e' }}>Exact Degree:</span> 
                                <strong style={{ color: '#ffffff' }}>{results["Sun"]?.degree != null ? results["Sun"].degree.toFixed(2) : "0.00"}°</strong>
                              </div>

                              <div className="traits-container" style={{ marginTop: '15px' }}>
                                <span className="trait-tag" data-element={results["Sun"]?.element}>{results["Sun"]?.element}</span>
                                <span className="trait-tag">{results["Sun"]?.modality}</span>
                              </div>
                              
                              <div className="somatic-highlight" style={{ marginTop: '15px', color: '#ffca28', fontWeight: 'bold' }}>
                                Somatic Node: {results["Sun"]?.somatic}
                              </div>
                            </div>

                            <div className="card-scripture-content" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #30363d' }}>
                              <h4 style={{ color: '#ffca28', letterSpacing: '2px', borderBottom: '1px solid rgba(255,202,40,0.3)', paddingBottom: '5px', marginBottom: '15px' }}>
                                ✨ YOUR ADONISIAN SUN SCRIPTURE (FREE SAMPLE)
                              </h4>
                            {(() => {
                                const safeSign = results["Sun"]?.sign ? results["Sun"].sign.trim() : "";
                                const safeHouse = results["Sun"]?.house ? String(results["Sun"].house).trim() : "";    
                                const signKey = `Sun_${safeSign}`;
                                const houseKey = `Sun_${safeHouse}`;

                                return (
                                  <div>
                                    <p style={{ marginBottom: '12px', fontSize: '0.95rem', color: '#f5fafa', lineHeight: '1.6' }}>
                                      {signLibrary[signKey] || `Spirit channeling for ${signKey} in progress...`}
                                    </p>
                                    <p style={{ marginBottom: '12px', fontSize: '0.95rem', color: '#f5fafa', lineHeight: '1.6' }}>
                                      {houseLibrary[houseKey] || `House alignment for ${houseKey} pending...`}
                                    </p>
                                    <p style={{ fontStyle: 'italic', color: '#00d4ff', fontSize: '0.95rem', lineHeight: '1.6' }}>
                                      {somaticLibrary[signKey] || `Somatic protocol for ${signKey} pending...`}
                                    </p>
                                  </div>
                                );
                              })()}
                            </div>

                            <div style={{ marginTop: '25px', padding: '15px', background: 'rgba(0, 212, 255, 0.05)', border: '1px dashed #00d4ff', borderRadius: '6px', textAlign: 'center' }}>
                              <p style={{ color: '#00d4ff', fontSize: '0.95rem', margin: '0 0 10px 0', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                Did your identity just shatter? 🕳️🐇
                              </p>
                              <p style={{ color: '#c9d1d9', fontSize: '0.85rem', margin: 0, lineHeight: '1.6' }}>
                                Screenshot this reality check, post it to your IG story, and tag <strong style={{ color: '#ffffff' }}>@TheAdonisian</strong> to wake up your network. Let them see the matrix.
                              </p>
                            </div>

                          </div>

                          <div className="funnel-gate-container" style={{ width: '100%', padding: '40px', boxSizing: 'border-box', background: 'rgba(255,255,255,0.02)', border: '1px dashed #30363d', borderRadius: '12px', textAlign: 'center' }}>                        
                            <h2 style={{ color: 'white', fontSize: '1.8rem' }}>Your Life Is Not An Approximation. 🌌</h2>
                            <p style={{ color: '#8b949e', maxWidth: '600px', margin: '15px auto', fontSize: '1rem', lineHeight: '1.6' }}>
                                Now that you know the truth about your Sun placement, you have only scratched the surface. Your physical vehicle, spinal pathways, and cerebrospinal fluid flow are governed by 12 other hidden keys.
                            </p>
                                
                                <h3 style={{ color: '#00d4ff', marginBottom: '10px', marginTop: '25px' }}>Initialize Your Profile to Begin Year 1</h3>
                                <p style={{ color: '#8b949e', marginBottom: '25px', fontSize: '0.95rem' }}>Create your free account to save this map, decode your custom scripture, and begin the 33-Level Gamified Ascendance track in The Sanctuary.</p>
                                
                                <div style={{ maxWidth: '400px', margin: '0 auto', background: '#0d1117', padding: '25px', borderRadius: '8px', border: '1px solid #30363d' }}>
                                  <Auth />
                                </div>
                              </div>    
                            </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* --- THE DEEPER NARRATIVE MOVED BELOW THE CALCULATOR --- */}
                <div className="funnel-narrative-container" style={{ maxWidth: '90%', margin: '40px auto 20px auto', padding: '0 20px', textAlign: 'left' }}>
                  {!results && (
                    <div style={{ width: '100%', maxWidth: '320px', aspectRatio: '1/1', margin: '0 auto 30px auto', background: '#0d1117', padding: '10px', borderRadius: '50%', border: '1px solid #30363d', boxShadow: '0 0 20px rgba(0, 212, 255, 0.05)', boxSizing: 'border-box' }}>            
                    <svg viewBox="0 0 100 100" aria-label="Astro-Somatic™ 13-Sign Astrology Wheel demonstrating Ophiuchus True Placement" role="img" style={{ transform: `rotate(${passiveRotation}deg)`, transformOrigin: '50% 50%' }}>
                        <title>Astro-Somatic™ 13-Sign Astrology Wheel</title>
                                      {ADONISIAN_WHEEL_CONFIG.map((sign) => {
                          const startAngle = (sign.start - 90) * (Math.PI / 180);
                          const endAngle = (sign.end - 90) * (Math.PI / 180);
                          const midAngle = ((sign.start + (sign.end < sign.start ? sign.end + 360 : sign.end)) / 2 - 90) * (Math.PI / 180);
                          const lx = 50 + 42 * Math.cos(midAngle);
                          const ly = 50 + 42 * Math.sin(midAngle);
                          const sx = 50 + 30 * Math.cos(midAngle);
                          const sy = 50 + 30 * Math.sin(midAngle);

                          return (
                            <g key={`home-wheel-${sign.name}`}>
                              <path d={`M 50 50 L ${50 + 45 * Math.cos(startAngle)} ${50 + 45 * Math.sin(startAngle)}`} stroke="#21262d" strokeWidth="0.15" />
                              <text x={sx} y={sy} fontSize="3.5" fill="#ffca28" textAnchor="middle" dominantBaseline="middle">{sign.symbol}</text>
                              <text x={lx} y={ly} fontSize="1.8" fill="#8b949e" textAnchor="middle" transform={`rotate(${(midAngle * 180 / Math.PI) + 90}, ${lx}, ${ly})`}>
                                {sign.name.toUpperCase()}
                              </text>
                            </g>
                          );
                        })}
                      </svg>
                    </div>
                  )}

                  <p style={{ color: '#ffffff', fontSize: '1.1rem', lineHeight: '1.7', marginBottom: '25px', opacity: 0.95 }}>
                    From a classical astrological standpoint, the Sun represents your core ego, while the Rising Sign is your physical vehicle—the mask worn at the horizon. But the disruption here is far more radical. Traditional astrology forces the cosmos into arbitrary, mathematically even 30-degree slices drawn up in the 2nd century. Over the last 2,000 years, the stars have shifted. By utilizing exact, real-time astronomical boundaries processed via the <strong>Swiss Ephemeris runtime path</strong>, we expose a shocking truth: <strong>the vast majority of the world is living under a completely false identity.</strong>
                  </p>

                  <div style={{ background: 'rgba(255, 202, 40, 0.05)', borderLeft: '4px solid #ffca28', padding: '15px 20px', borderRadius: '4px', marginBottom: '35px' }}>
                    <p style={{ color: '#ffca28', margin: 0, fontSize: '1rem', fontStyle: 'italic', lineHeight: '1.5' }}>
                      "Imagine walking through life believing you are a fierce, fiery November Sagittarius, only for real-time astronomy to reveal that the Sun was sitting dead-center in the deep, unmapped waters of <strong>Ophiuchus</strong> when you drew your first breath. It is a reality-shattering identity crisis."
                    </p>
                  </div>

                  <h3 style={{ color: '#00d4ff', fontSize: '1.5rem', fontWeight: '400', marginBottom: '12px' }}>
                    The Missing Macro-Layer to Bio-Hacking
                  </h3>
                  <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6', marginBottom: '35px' }}>
                    Modern performance icons emphasize intense daily routines to optimize endogenous factors—influencing internal biological fluid mechanics and nervous system resilience from the inside out. <strong>But they completely miss the macro-environmental triggers.</strong> The massive planetary bodies in our cosmos exert continuous, calculable physical forces on our physiology. The Adonisian framework maps these real-time celestial alignments directly to your <strong>Cerebrospinal Fluid (CSF) circulation rhythms</strong> and localized joint matrices, unlocking an exact blueprint for physical recovery and somatic mastery.
                  </p>

{/* 👇 THE VIP MANIFESTO BANNER 👇 */}
                <div style={{
                  margin: '40px auto 60px auto',
                  width: '95%', /* Forces it to stay inside the mobile screen */
                  maxWidth: '1000px',
                  boxSizing: 'border-box', /* Keeps padding inside the width boundary */
                  background: 'linear-gradient(135deg, #0d1117 0%, #170d1c 50%, #0d1117 100%)',
                  border: '1px solid #ffb3ff',
                  borderRadius: '12px',
                  padding: 'clamp(30px, 5vw, 50px) clamp(20px, 5vw, 40px)', /* Fluid padding for mobile vs desktop */
                  boxShadow: '0 0 40px rgba(255, 179, 255, 0.12)',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  
                  {/* The Greeting */}
                  <h2 style={{ 
                  color: '#ffca28', 
                  fontSize: 'clamp(1.2rem, 4vw, 1.6rem)', 
                  textTransform: 'uppercase', 
                  letterSpacing: '2px', 
                  marginBottom: '15px',
                  lineHeight: '1.4'
                  }}>
                    To the Beautiful Souls, Magnetic Minds, & Exhausted Providers... ✨
                  </h2>
                  
                  {/* The Disruptor Hook (Separated for maximum sleekness) */}
                  <h3 style={{
                    color: '#ffb3ff',
                    fontSize: 'clamp(1.05rem, 3vw, 1.3rem)',
                    fontStyle: 'italic',
                    fontWeight: 'bold',
                    marginBottom: '30px',
                    lineHeight: '1.6',
                    letterSpacing: '1px'
                  }}>
                    "Adonisian Astrology is the ultimate disruptor because it's the last thing we have thought to disrupt: HEAVEN!"
                  </h3>
                  
                  <p style={{ color: '#e2e8f0', fontSize: '1rem', maxWidth: '800px', margin: '0 auto 40px auto', lineHeight: '1.8' }}>
                    My name is Adonis. I am an <span onClick={() => setShowAstroModal(true)} style={{ color: '#00d4ff', cursor: 'pointer', borderBottom: '1px dashed #00d4ff', fontWeight: 'bold' }}>astrosomatic architect</span> who has navigated the profound grief, systemic friction, and relentless grind of everyday survival—witnessing firsthand the collective exhaustion and physical toll of the simulation. In response, I built this matrix. Whether you found me through a chance encounter, a YouTube transmission, or an undeniable cosmic gravity, you are here for a reason. What I do extends far beyond traditional astrology. It is a physical reclamation.
                  </p>

                  {/* THE ARCHITECTURE MANIFESTO */}
                  <div style={{ 
                    background: 'rgba(0,0,0,0.4)', 
                    border: '1px solid #30363d', 
                    borderRadius: '8px', 
                    padding: 'clamp(20px, 4vw, 40px)', 
                    marginBottom: '40px', 
                    textAlign: 'left',
                    boxSizing: 'border-box',
                    width: '100%' 
                  }}>
                    <h3 style={{ color: '#ffb3ff', fontSize: 'clamp(1.2rem, 3vw, 1.4rem)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '20px', textAlign: 'center' }}>
                      The Architecture of Alchemy
                    </h3>
                    <p style={{ color: '#c9d1d9', fontSize: '0.95rem', lineHeight: '1.7', marginBottom: '30px', textAlign: 'center', maxWidth: '750px', margin: '0 auto 30px auto' }}>
                      Adonisian Astrology is a highly specialized, esoteric system that seeks to bridge ancient wisdom traditions with modern understandings of neurochemistry. It is a practical 13-step framework for proven, individually variable results. <strong style={{ color: '#ffffff' }}>Ultimately, it is Alchemy.</strong>
                    </p>

                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', /* Dropped to 240px so it stacks perfectly on iPhones */
                      gap: '25px' 
                    }}>
                      
                      {/* Pillar 1 */}
                      <div style={{ borderLeft: '2px solid #00d4ff', paddingLeft: '15px' }}>
                        <h4 style={{ color: '#00d4ff', margin: '0 0 10px 0', fontSize: '1.05rem' }}>1. 13-Sign True Astronomy</h4>
                        <p style={{ color: '#8b949e', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                          Unlike conventional Western astrology, we utilize a 13-sign celestial model based on observable, astronomical fact. What you see in the skies matches the paper—an exact mirror of the heavens.
                        </p>
                      </div>

                      {/* Pillar 2 */}
                      <div style={{ borderLeft: '2px solid #ffca28', paddingLeft: '15px' }}>
                        <h4 style={{ color: '#ffca28', margin: '0 0 10px 0', fontSize: '1.05rem' }}>2. Mayan Somatic Correspondence</h4>
                        <p style={{ color: '#8b949e', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                          We establish a direct somato-astrological correlation, linking the 13 astronomical signs to the 13 major joints of the human body, drawing directly upon ancient Mayan physiological wisdom.
                        </p>
                      </div>

                      {/* Pillar 3 */}
                      <div style={{ borderLeft: '2px solid #00ff41', paddingLeft: '15px' }}>
                        <h4 style={{ color: '#00ff41', margin: '0 0 10px 0', fontSize: '1.05rem' }}>3. Hindu Energetic Integration</h4>
                        <p style={{ color: '#8b949e', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                          Deepening the mind-body-spirit connection, the framework associates these specific physical joints and celestial placements with the energetic Chakras understood in Hindu traditions.
                        </p>
                      </div>

                      {/* Pillar 4 */}
                      <div style={{ borderLeft: '2px solid #ffb3ff', paddingLeft: '15px' }}>
                        <h4 style={{ color: '#ffb3ff', margin: '0 0 10px 0', fontSize: '1.05rem' }}>4. Neuro-Spiritual Mechanism</h4>
                        <p style={{ color: '#8b949e', fontSize: '0.9rem', lineHeight: '1.6', margin: 0 }}>
                          This astrological-somatic connection is harnessed to physically stimulate Cerebrospinal Fluid (CSF). This targeted flow initiates the endogenous production of DMT—the "God Molecule"—aiming for profound altered states of consciousness.
                        </p>
                      </div>

                    </div>
                  </div>

                  <p style={{ color: '#e2e8f0', fontSize: '1rem', maxWidth: '750px', margin: '0 auto 25px auto', lineHeight: '1.8' }}>
                    If I promised you a reading, or if you are ready to unlock your true celestial coordinates, your somatic cheat codes, and your absolute sovereignty, your VIP experience awaits. Reach out directly to claim your space.
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginTop: '30px' }}>
                    <a href="mailto:adonis@adonisian.com" style={{ 
                      color: '#00d4ff', 
                      textDecoration: 'none', 
                      border: '1px solid #00d4ff', 
                      padding: '12px 25px', 
                      borderRadius: '4px', 
                      textTransform: 'uppercase',
                      fontWeight: 'bold', 
                      letterSpacing: '2px', 
                      fontSize: '1rem' 
                    }}>
                      ✉️ Request VIP Access
                    </a>
                  </div>
                </div>
                {/* 👆 END VIP MANIFESTO BANNER 👆 */}

<h3 style={{ color: '#ffffff', fontSize: '1.4rem', fontWeight: '500', marginBottom: '20px', textAlign: 'center', letterSpacing: '1px' }}>
                    Enter Your Email to Become a Member and Command Your Matrix with These Free Tools:
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                    <div style={{ background: '#0d1117', border: '1px solid #30363d', padding: '20px', borderRadius: '8px' }}>
                      <div style={{ color: '#00d4ff', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '10px' }}>1</div>
                      <h4 style={{ color: '#ffffff', margin: '0 0 8px 0' }}>Calculate Your Reality</h4>
                      <p style={{ color: '#8b949e', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
                        Input your precise birth year, month, day, exact time converted to <strong>UTC</strong>, and your coordinates. Discover your true astronomical placements instantly.
                      </p>
                    </div>

                    <div style={{ background: '#0d1117', border: '1px solid #30363d', padding: '20px', borderRadius: '8px' }}>
                      <div style={{ color: '#ffca28', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '10px' }}>2</div>
                      <h4 style={{ color: '#ffffff', margin: '0 0 8px 0' }}>Claim Your Free Keys</h4>
                      <p style={{ color: '#8b949e', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
                        See your interactive cosmic cross-section. Review your exact elemental properties, somatic physical nodes, and planetary modalities completely for free.
                      </p>
                    </div>

                    <div style={{ background: '#0d1117', border: '1px solid #30363d', padding: '20px', borderRadius: '8px' }}>
                      <div style={{ color: '#00d4ff', fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '10px' }}>3</div>
                      <h4 style={{ color: '#ffffff', margin: '0 0 8px 0' }}>Track Your Ascendance</h4>
                      <p style={{ color: '#8b949e', fontSize: '0.85rem', margin: 0, lineHeight: '1.5' }}>
                        Create a profile to access <strong>The Sanctuary</strong>. Log your daily spinal meditation practices, conquer the 33-Vertebrae track, and unlock your personalized scriptural depths.
                      </p>
                    </div>
                  </div>

                  {/* 👇 THE MISSING EMAIL CAPTURE BOX 👇 */}
                  <div style={{ maxWidth: '400px', margin: '0 auto 60px auto', background: '#05070a', padding: '30px', borderRadius: '8px', border: '1px solid #30363d', boxShadow: '0 0 20px rgba(0, 212, 255, 0.05)' }}>
                    <h4 style={{ color: '#ffffff', textAlign: 'center', margin: '0 0 20px 0', fontSize: '1.1rem' }}>Initialize Your Free Account</h4>
                    <Auth />
                  </div>

                  {/* --- DIRECTORY OF THE SYSTEM (DIVIDED HORIZONTAL SECTIONS) --- */}
                  <div style={{ width: '100%', maxWidth: '800px', margin: '60px auto', padding: '0 20px', boxSizing: 'border-box', textAlign: 'left' }}>
                    <div style={{ borderBottom: '1px solid #30363d', paddingBottom: '15px', marginBottom: '30px' }}>
                      <h3 style={{ color: '#ffffff', fontSize: '1.5rem', margin: 0, letterSpacing: '1px' }}>
                        Explore the System
                      </h3>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '35px' }}>
                      
                      {/* Section 1: The Creed */}
                      <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '30px', borderBottom: '1px solid #1f2428' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '1.8rem' }}>⚡</span>
                          <h4 style={{ color: '#ffffff', fontSize: '1.2rem', margin: 0 }}>Wake Up (The Creed)</h4>
                        </div>
                        <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 15px 0' }}>
                          Read the foundational manifesto of Adonisian Astrology. Understand the necessity of bridging ancient cosmic awareness with neuroscientific consciousness to escape the simulation.
                        </p>
                        <div><Link to="/creed" style={{ color: '#9900ff', textDecoration: 'none', fontWeight: 'bold' }}>Read the Creed ➝</Link></div>
                      </div>

                      {/* Section 2: Monthly Horoscopes */}
                      <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '30px', borderBottom: '1px solid #1f2428' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '1.8rem' }}>🔮</span>
                          <h4 style={{ color: '#ffffff', fontSize: '1.2rem', margin: 0 }}>Monthly Horoscopes</h4>
                        </div>
                        <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 15px 0' }}>
                          Detailed, long-form monthly horoscopes synchronized with actual planetary transits. Navigate your somatic focus, financial reality checks, and energetic shifts based on your true sign.
                        </p>
                        <div><Link to="/horoscopes" style={{ color: '#f6ff00', textDecoration: 'none', fontWeight: 'bold' }}>Read Your Horoscope ➝</Link></div>
                      </div>

                      {/* Section 3: 26 Moon Calendar */}
                      <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '30px', borderBottom: '1px solid #1f2428' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '1.8rem' }}>🌑</span>
                          <h4 style={{ color: '#ffffff', fontSize: '1.2rem', margin: 0 }}>26 Moon Calendar</h4>
                        </div>
                        <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 15px 0' }}>
                          The moon does not adhere to a 12-month calendar. Calculate your exact lunar birth phase, discover your celestial animal totem, and sync with the true 26-moon cycle.
                        </p>
                        <div><Link to="/moon-calendar" style={{ color: '#00ff41', textDecoration: 'none', fontWeight: 'bold' }}>Calculate Lunar Phase ➝</Link></div>
                      </div>

                      {/* Section 4: Sign Ages & The Great Year */}
                      <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '30px', borderBottom: '1px solid #1f2428' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '1.8rem' }}>🪐</span>
                          <h4 style={{ color: '#ffffff', fontSize: '1.2rem', margin: 0 }}>Sign Ages & The Great Year</h4>
                        </div>
                        <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 15px 0' }}>
                          Track the macro-movements of human history. Understand the precession of the equinoxes, the transition of astrological ages, and how humanity is evolving through the Great Year.
                        </p>
                        <div><Link to="/great-year" style={{ color: '#28fbff', textDecoration: 'none', fontWeight: 'bold' }}>Explore the Great Year ➝</Link></div>
                      </div>

                      {/* Section 5: 13-Sign Ephemeris */}
                      <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '30px', borderBottom: '1px solid #1f2428' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '1.8rem' }}>🌌</span>
                          <h4 style={{ color: '#ffffff', fontSize: '1.2rem', margin: 0 }}>13-Sign Ephemeris</h4>
                        </div>
                        <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 15px 0' }}>
                          Access the raw astronomical data. View the exact, unadulterated planetary coordinates and Swiss Ephemeris mathematics that power the framework.
                        </p>
                        <div><Link to="/ephemeris" style={{ color: '#b3ffe6', textDecoration: 'none', fontWeight: 'bold' }}>View Ephemeris ➝</Link></div>
                      </div>

                      {/* Section 6: About the Data */}
                      <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: '30px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                          <span style={{ fontSize: '1.8rem' }}>📖</span>
                          <h4 style={{ color: '#ffffff', fontSize: '1.2rem', margin: 0 }}>About the Data</h4>
                        </div>
                        <p style={{ color: '#8b949e', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 15px 0' }}>
                          Learn about the core geometry of the sky, the history of the 13th sign Ophiuchus, and why modern western astrology relies on a broken 2,000-year-old seasonal calendar.
                        </p>
                        <div><Link to="/about" style={{ color: '#0073ff', textDecoration: 'none', fontWeight: 'bold' }}>Read About the Math ➝</Link></div>
                      </div>

                    </div>
                  </div>

                  {/* --- DAILY SOMATIC WEATHER MODULE --- */}
                  <div style={{ width: '100%', margin: '50px 0 30px 0', padding: '22px', boxSizing: 'border-box', background: 'rgba(0, 212, 255, 0.03)', borderTop: '1px solid #00d4ff', borderBottom: '1px solid #00d4ff' }}>  
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                      <h3 style={{ color: '#00d4ff', margin: 0, fontSize: '1.4rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                        🌍 Global Somatic Weather
                      </h3>
                      
                      {/* TIME TRAVEL CONTROLS */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 100%', justifyContent: 'center' }}>       
                        <button 
                          onClick={() => setWeatherDateOffset(prev => Math.max(prev - 1, -1))}
                          disabled={weatherDateOffset <= -1}
                          style={{ 
                            background: 'transparent', 
                            border: 'none', 
                            color: '#ffca28', 
                            fontSize: '1.2rem',
                            cursor: weatherDateOffset <= -1 ? 'not-allowed' : 'pointer',
                            opacity: weatherDateOffset <= -1 ? 0.3 : 1
                          }}>
                          ◀
                        </button>
                        
                        <span style={{ color: '#8b949e', fontSize: '0.7rem', border: '1px solid #30363d', padding: '4px 15px', borderRadius: '15px', minWidth: '120px', textAlign: 'center' }}>
                          {(() => {
                            const d = new Date();
                            d.setDate(d.getDate() + weatherDateOffset);
                            if (weatherDateOffset === 0) return "TODAY";
                            if (weatherDateOffset === -1) return "YESTERDAY";
                            if (weatherDateOffset === 1) return "TOMORROW";
                            return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                          })()}
                        </span>
                        
                        <button 
                          onClick={() => setWeatherDateOffset(prev => Math.min(prev + 1, 1))}
                          disabled={weatherDateOffset >= 1}
                          style={{ 
                            background: 'transparent', 
                            border: 'none', 
                            color: '#ffca28', 
                            fontSize: '1.2rem',
                            cursor: weatherDateOffset >= 1 ? 'not-allowed' : 'pointer',
                            opacity: weatherDateOffset >= 1 ? 0.3 : 1
                          }}>
                          ▶
                        </button>
                      </div>
                    </div>
                    
                    {/* WEATHER DATA DISPLAY */}
                    {dailyWeather && dailyWeather.title !== "Signal Lost" ? (
                      <div>
                        <h4 style={{ color: '#ffca28', margin: '0 0 10px 0', fontSize: '1.1rem' }}>{dailyWeather.title}</h4>
                        <p style={{ color: '#c9d1d9', fontSize: '1rem', lineHeight: '1.6', margin: '0 0 15px 0' }}>
                          {dailyWeather.reading}
                        </p>
                        <div style={{ display: 'inline-block', background: '#0d1117', border: '1px solid #ffca28', padding: '8px 15px', borderRadius: '4px' }}>
                          <strong style={{ color: '#8b949e', fontSize: '0.85rem', textTransform: 'uppercase' }}>Primary Somatic Focus: </strong>
                          <span style={{ color: '#ffca28', fontWeight: 'bold' }}>{dailyWeather.somatic_focus}</span>
                        </div>
                      </div>
                    ) : (
                      <p style={{ color: '#8b949e', fontStyle: 'italic', margin: 0, padding: '20px 0' }}>
                        {dailyWeather ? dailyWeather.reading : "Compiling planetary telemetry and cerebrospinal fluid markers. Please hold..."}
                      </p>
                    )}
                  </div>
                </div>
              </main>
            } />
            
            {/* --- NEW DEDICATED MEMBER SANCTUARY ROUTE --- */}
            <Route path="/sanctuary" element={
              session && matrixProfile ? (
                <AscendanceDashboard 
                  profile={matrixProfile} 
                  onLogMeditation={logMeditation} 
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '100px 20px', color: '#ffca28', minHeight: '60vh' }}>
                  <Helmet>
                    <title>The Sanctuary | Vault Locked</title>
                  </Helmet>
                  <h2 style={{ textTransform: 'uppercase', letterSpacing: '2px', fontSize: '2rem' }}>Vault Locked</h2>
                  <p style={{ color: '#8b949e', marginTop: '15px', fontSize: '1.1rem' }}>You must initiate a profile and authenticate to access The Sanctuary.</p>
                  <button 
                    onClick={() => setShowLoginModal(true)}
                    style={{ marginTop: '30px', backgroundColor: '#00d4ff', color: '#0d1117', padding: '12px 24px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '1.1rem' }}
                  >
                    Authenticate Now
                  </button>
                </div>
              )
            } />

            {/* --- LEGAL ROUTES & DATA ROUTES --- */}
            <Route path="/about" element={<About />} />
            <Route path="/creed" element={<AdonisianCreed />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/moon-calendar" element={<MoonCalculator />} />
            <Route path="/great-year" element={<GreatYearTracker />} />
            <Route path="/ephemeris" element={<Ephemeris />} />
            <Route path="/natal-blueprint" element={<NatalOracle session={session} signLibrary={signLibrary} houseLibrary={houseLibrary} somaticLibrary={somaticLibrary} />} />            <Route path="/oracle" element={<SynastryOracle session={session} />} />
            <Route path="/horoscopes" element={<Horoscopes session={session} isPremium={userPerms.includes("horoscope_subscription")} />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/services" element={<Services />} />
          </Routes>
        </div>

        {/* 👇 THE NEW GLOBAL CATCH 👇 */}
        <GlobalCheckoutBanner 
          session={session} 
          onCheckout={handleGlobalCheckout} 
        />

        {/* ========================================= */}
        {/* --- THE LEFT-ALIGNED GRID FOOTER ---      */}
        {/* ========================================= */}
      <footer style={{ width: '100%', padding: '50px 30px', boxSizing: 'border-box', borderTop: '1px solid #30363d', backgroundColor: '#0d1117', marginTop: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '30px', maxWidth: '1000px', margin: '0 auto', textAlign: 'left' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h4 style={{ color: '#ffffff', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.9rem' }}>The Matrix</h4>
              <Link to="/" style={{ color: '#ff3328', textDecoration: 'none', fontSize: '0.9rem' }}>📟 The Portal</Link>
              <Link to="/sanctuary" style={{ color: '#ffffff', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 'bold' }}>🏛️ The Sanctuary (Member Login)</Link>
              <Link to="/services" style={{ color: '#ffca28', textDecoration: 'none', fontSize: '0.9rem' }}>🗝️ Services</Link>
              <Link to="/ephemeris" style={{ color: '#b3ffe6', textDecoration: 'none', fontSize: '0.9rem' }}>🌌 13-Sign Ephemeris</Link>
              <Link to="/horoscopes" style={{ color: '#f6ff00', textDecoration: 'none', fontSize: '0.9rem' }}>🔮 Monthly Horoscopes</Link>
              <Link to="/moon-calendar" style={{ color: '#00ff41', textDecoration: 'none', fontSize: '0.9rem' }}>🌑 26 Moon Calendar</Link>
              <Link to="/great-year" style={{ color: '#28fbff', textDecoration: 'none', fontSize: '0.9rem' }}>🪐 Sign Ages & The Great Year</Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}> <h4 style={{ color: '#ffffff', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.9rem' }}>Knowledge</h4>
              <Link to="/about" style={{ color: '#0073ff', textDecoration: 'none', fontSize: '0.9rem' }}>📖 About the Data</Link>
              <Link to="/creed" style={{ color: '#9900ff', textDecoration: 'none', fontSize: '0.9rem' }}>⚡ Wake Up (The Creed)</Link>
              <Link to="/contact" style={{ color: '#ff4079', textDecoration: 'none', fontSize: '0.9rem' }}>📡 Transmission (Contact)</Link>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h4 style={{ color: '#ffffff', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.9rem' }}>Legal</h4>
              <Link to="/terms" style={{ color: '#8b949e', textDecoration: 'none', fontSize: '0.9rem' }}>Terms & Conditions</Link>
              <Link to="/privacy" style={{ color: '#8b949e', textDecoration: 'none', fontSize: '0.9rem' }}>Privacy Policy</Link>
            </div>
          </div>

          <div style={{ color: '#484f58', fontSize: '0.8rem', lineHeight: '1.5', maxWidth: '1000px', margin: '40px auto 0 auto', borderTop: '1px solid #1f2428', paddingTop: '20px', textAlign: 'left' }}>
            <p style={{ margin: '0 0 5px 0' }}>© {new Date().getFullYear()} Adonisian Astrology™ by Adonis Ramirez. All rights reserved.</p>
            <p style={{ margin: 0 }}>Developed in the San Francisco Bay Area, CA. The Adonisian Somatic Blueprint and Ascendance Progress System are proprietary trademarks.</p>
          </div>
        </footer>
      
      </div> 
    </Router>
    </HelmetProvider>
  );
}

export default App;